"use client";

import {
  ApolloLink,
  HttpLink,
  NormalizedCacheObject,
  split,
} from "@apollo/client";
import { getMainDefinition } from "@apollo/client/utilities";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import {
  ApolloNextAppProvider,
  ApolloClient,
  InMemoryCache,
  SSRMultipartLink,
} from "@apollo/experimental-nextjs-app-support";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import {
  fetchNewTokenAndRetry,
  fetchNewTokenByRefreshToken,
  getCompanyProfileId,
} from "@utils/auth";
import { setMaintenanceMode } from "@utils/maintenanceMode";
import { SKIP_AUTHENTICATION_WHITELIST } from "./whitelist";
import { getRuntimeConfig } from "@lib/runtimeEnvClient";

const UNAUTHORIZED_WS_CLOSE_CODE = 4403;

// Lazy initialization - only create when first accessed
let _apolloClient: ApolloClient<NormalizedCacheObject> | null = null;

function getApolloClient(): ApolloClient<NormalizedCacheObject> {
  if (!_apolloClient) {
    _apolloClient = makeClient();
  }

  return _apolloClient;
}

function makeClient(ssrCompanyProfileId?: string) {
  const backendUrl = getRuntimeConfig("NEXT_PUBLIC_BACKEND_URL");

  const httpLink = new HttpLink({
    uri: `${backendUrl}/graphql`,
    credentials: "include",
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: `${backendUrl.replace("http", "ws")}/graphql`,
      connectionParams: {
        companyProfileId: getCompanyProfileId() ?? ssrCompanyProfileId,
      },
      retryAttempts: 10, // Number of reconnect attempts
      lazy: true, // Only connect when needed
      on: {
        closed: (event: any) => {
          console.warn("WebSocket closed", event);
          if (event.code === UNAUTHORIZED_WS_CLOSE_CODE) {
            console.error(
              "WebSocket connection closed due to authentication error (4403). Attempting to refresh token."
            );
            fetchNewTokenByRefreshToken(getApolloClient()).catch((err) => {
              console.error("Failed to refresh token", err);
            });
          }
        },
        error: (error) => {
          console.error("WebSocket error", error);
        },
      },
      shouldRetry: () => true, // Always retry on disconnect/error
    })
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);

      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink, // Use wsLink for subscriptions
    httpLink // Use httpLink for all other operations
  );

  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
          console.debug(
            `[Apollo GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          )
        );

        if (
          graphQLErrors.some(
            (e) =>
              e.message.includes("Session is expired") ||
              e.message.includes("Access denied")
          ) &&
          !!_apolloClient
        ) {
          console.debug(`[Apollo Session expired], graphql error`);

          return fetchNewTokenAndRetry(getApolloClient(), operation, forward);
        }
      }

      if (networkError) {
        console.debug(
          `[Apollo Network error]: message:${
            networkError.message
          }, statusCode:${(networkError as any).statusCode}`
        );

        // Expired
        if ((networkError as any).statusCode === 401) {
          console.debug(`network error 401, fetch new token and retry`);

          return fetchNewTokenAndRetry(getApolloClient(), operation, forward);
        }

        // Maintenance mode
        if ((networkError as any).statusCode === 512) {
          setMaintenanceMode(true);
        }
      }
    }
  );

  const authLink = setContext(
    ({ operationName }, { headers: contextHeaders }) => {
      // Some GraphQL operations must not include auth information
      if (!operationName) {
        return {};
      }

      const mustSkipAuthentication =
        SKIP_AUTHENTICATION_WHITELIST.includes(operationName);

      if (mustSkipAuthentication) {
        return {};
      }

      return {
        headers: {
          ...contextHeaders,
          "X-Company-Profile-Id": getCompanyProfileId() || ssrCompanyProfileId,
          "X-Session-Id": sessionStorage.getItem("sessionId"),
        },
      };
    }
  );

  const environment = getRuntimeConfig("NEXT_PUBLIC_ENVIRONMENT", "local");

  return new ApolloClient({
    cache: new InMemoryCache(),
    credentials: "include",
    connectToDevTools: environment !== "production",
    link:
      typeof window === "undefined"
        ? ApolloLink.from([
            new SSRMultipartLink({
              stripDefer: true,
            }),
            authLink,
            splitLink,
          ])
        : ApolloLink.from([errorLink, authLink, splitLink]),
  });
}

type Props = {
  companyProfileId?: string;
};

export function ApolloProvider({
  children,
  companyProfileId,
}: React.PropsWithChildren<Props>) {
  return (
    <ApolloNextAppProvider makeClient={() => makeClient(companyProfileId)}>
      {children}
    </ApolloNextAppProvider>
  );
}
